import React, { useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  Divider,
  Drawer,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import {
  CreateBodyMeasurementPayload,
  createBodyMeasurement,
} from "../../services/BodyMeasurementService";

interface BodyMeasurementDrawerProps {
  open: boolean;
  userId: string;
  onClose: () => void;
  onCreated: () => void;
}

type FormState = Record<string, string>;

const emptyForm: FormState = {
  measuredAt: "",
  weightKg: "",
  skeletalMuscleKg: "",
  bodyFatKg: "",
  heightCm: "",
  bodyFatPct: "",
  skeletalMusclePct: "",
  visceralFatLevel: "",
  bodyAge: "",
  bmi: "",
  restingMetabolismKcal: "",
  note: "",
};

/** Numeric fields the backend requires on every scan. */
const REQUIRED_NUMERIC = ["weightKg", "skeletalMuscleKg", "bodyFatKg"] as const;

const numberFields: {
  name: keyof CreateBodyMeasurementPayload;
  label: string;
}[] = [
  { name: "weightKg", label: "Weight (kg) *" },
  { name: "skeletalMuscleKg", label: "Skeletal muscle (kg) *" },
  { name: "bodyFatKg", label: "Body fat (kg) *" },
  { name: "heightCm", label: "Height (cm)" },
  { name: "bmi", label: "BMI" },
  { name: "bodyFatPct", label: "Body fat (%)" },
  { name: "skeletalMusclePct", label: "Skeletal muscle (%)" },
  { name: "visceralFatLevel", label: "Visceral fat level" },
  { name: "bodyAge", label: "Body age" },
  { name: "restingMetabolismKcal", label: "Resting metabolism (kcal)" },
];

const BodyMeasurementDrawer: React.FC<BodyMeasurementDrawerProps> = ({
  open,
  userId,
  onClose,
  onCreated,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  const handleChange = (name: string, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const resetAndClose = () => {
    setForm(emptyForm);
    setErrors({});
    onClose();
  };

  const handleSubmit = async () => {
    const nextErrors: Record<string, boolean> = {};
    REQUIRED_NUMERIC.forEach((field) => {
      const raw = form[field];
      if (raw === "" || Number.isNaN(Number(raw))) nextErrors[field] = true;
    });
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      enqueueSnackbar(
        "Weight, skeletal muscle and body fat (kg) are required",
        {
          variant: "warning",
        }
      );
      return;
    }

    const payload: CreateBodyMeasurementPayload = {
      userId,
      weightKg: Number(form.weightKg),
      skeletalMuscleKg: Number(form.skeletalMuscleKg),
      bodyFatKg: Number(form.bodyFatKg),
    };
    if (form.measuredAt) payload.measuredAt = form.measuredAt;
    (
      [
        "heightCm",
        "bmi",
        "bodyFatPct",
        "skeletalMusclePct",
        "visceralFatLevel",
        "bodyAge",
        "restingMetabolismKcal",
      ] as (keyof CreateBodyMeasurementPayload)[]
    ).forEach((field) => {
      const raw = form[field];
      if (raw !== "" && !Number.isNaN(Number(raw))) {
        (payload[field] as number) = Number(raw);
      }
    });
    if (form.note.trim()) payload.note = form.note.trim();

    try {
      setSaving(true);
      await createBodyMeasurement(payload);
      enqueueSnackbar("Measurement added", { variant: "success" });
      setForm(emptyForm);
      setErrors({});
      onCreated();
      onClose();
    } catch (error: any) {
      const message =
        error?.response?.data?.data ||
        error?.response?.data?.message ||
        "Failed to add measurement";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={resetAndClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: 420 },
          p: 3,
          backgroundColor: theme.palette.background.default,
        },
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h6">Add measurement</Typography>
        <IconButton onClick={resetAndClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 0.5, color: theme.palette.text.secondary }}
          >
            Measured at
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="datetime-local"
            value={form.measuredAt}
            onChange={(e) => handleChange("measuredAt", e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Leave blank to use the current time"
          />
        </Grid>

        {numberFields.map((field) => (
          <Grid item xs={6} key={field.name}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 0.5, color: theme.palette.text.secondary }}
            >
              {field.label}
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              value={form[field.name]}
              onChange={(e) => handleChange(field.name, e.target.value)}
              error={Boolean(errors[field.name])}
            />
          </Grid>
        ))}

        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 0.5, color: theme.palette.text.secondary }}
          >
            Note
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={form.note}
            onChange={(e) => handleChange("note", e.target.value)}
          />
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
        <Button variant="outlined" onClick={resetAndClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? "Saving…" : "Save measurement"}
        </Button>
      </Box>
    </Drawer>
  );
};

export default BodyMeasurementDrawer;
